import React, { useRef, useState, ReactNode } from 'react'
import { Box, Typography, IconButton, Tooltip, TextField, Checkbox } from '@mui/material'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import QuestionBadge from './QuestionBadge'
import { FS_SM, FS_XS, ICON_SM } from '../common/theme'

// Shared flat-list item shape: a section divider or a question row.
export type FlatItem =
  | { type: 'section'; sectionId: string }
  | { type: 'question'; sha: string }

export interface SectionMeta { id: string; name: string }

// Minimal shape every question must satisfy to be rendered by this list.
// Both `Question` (bid manager) and `ExtractedQuestion` (import wizard)
// satisfy this structurally - extra fields just flow through.
export interface SectionedListQuestion {
  questionsha: string
  question?: string
  numbering?: string
}

// Gold drop-indicator bar overlaid at the top of a row when it's the
// active drop target. Positioned absolutely so it sits cleanly over the
// gap between rows regardless of the row's own border styling.
function DropIndicator() {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: -2, left: 0, right: 0,
        height: 3,
        bgcolor: '#FFC107',
        zIndex: 2,
        pointerEvents: 'none',
      }}
    />
  )
}

interface SectionedQuestionListProps<Q extends SectionedListQuestion> {
  items: FlatItem[]
  setItems: React.Dispatch<React.SetStateAction<FlatItem[]>>
  sections: SectionMeta[]
  setSections: React.Dispatch<React.SetStateAction<SectionMeta[]>>
  // sha → question. Anything implementing SectionedListQuestion.
  qMap: Record<string, Q>
  // Selection set (controlled). Toggled via onToggleSelect.
  selectedShas: Set<string>
  onToggleSelect: (sha: string) => void
  // Whether to filter a question out of the list (e.g. ExtractedQuestion
  // with include=false). When omitted every question in `items` renders.
  isQuestionVisible?: (q: Q) => boolean
  // Per-question numbering edit. When provided, the numbering renders as
  // a QuestionBadge that calls back on commit; otherwise read-only.
  onNumberingCommit?: (sha: string, value: string) => void
  // Section deletion (caller owns the data mutation).
  onDeleteSection?: (id: string) => void
  // Optional render slot on the right side of each question row - used
  // by BidManageQuestionsTab for StatePicker + TaWordLimit.
  rowEndSlot?: (q: Q, sha: string) => ReactNode
  // Called after any list mutation (drag/drop reorder, section rename,
  // section delete) so callers can flip an isDirty flag.
  onChange?: () => void
  // Enables multi-question drag: if the dragged question is part of a
  // selection of 2+, the whole selection moves as a block.
  groupDragEnabled?: boolean
  // testid prefix for the rendered rows (defaults to "sql").
  testIdPrefix?: string
}

// Shared sectioned, draggable question list. Used by both
// BidManageQuestionsTab (saved-project editing) and BiqOrganizePhase
// (pre-import organize step). Owns section rename state, drag-drop
// reorder, indentation, and the drop indicator. Toolbar / bottom buttons
// remain the caller's responsibility.
export default function SectionedQuestionList<Q extends SectionedListQuestion>({
  items, setItems, sections, setSections, qMap,
  selectedShas, onToggleSelect,
  isQuestionVisible, onNumberingCommit, onDeleteSection,
  rowEndSlot, onChange, groupDragEnabled,
  testIdPrefix = 'sql',
}: SectionedQuestionListProps<Q>) {
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')

  const dragIdxRef = useRef<number | null>(null)
  const dragGroupShasRef = useRef<Set<string> | null>(null)
  const [overIdx, setOverIdx] = useState<number | null>(null)

  // ── Section rename ───────────────────────────────────────────────────────
  const startRename = (id: string, name: string) => {
    setEditingSectionId(id)
    setEditingName(name)
  }
  const commitRename = () => {
    if (!editingSectionId) return
    const name = editingName.trim() || 'Section'
    setSections(prev => prev.map(s => s.id === editingSectionId ? { ...s, name } : s))
    setEditingSectionId(null)
    onChange?.()
  }

  // ── Drag handlers ────────────────────────────────────────────────────────
  const handleDragStart = (e: React.DragEvent, idx: number) => {
    dragIdxRef.current = idx
    const item = items[idx]
    if (groupDragEnabled && item.type === 'question' && selectedShas.has(item.sha) && selectedShas.size > 1) {
      dragGroupShasRef.current = new Set(selectedShas)
    } else {
      dragGroupShasRef.current = null
    }
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(idx))
  }
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (dragIdxRef.current !== idx) setOverIdx(idx)
  }
  const handleDrop = (e: React.DragEvent, toIdx: number) => {
    e.preventDefault()
    const fromIdx = dragIdxRef.current
    const groupShas = dragGroupShasRef.current
    if (fromIdx === null) { setOverIdx(null); return }

    if (groupShas) {
      // Group drag - pull every selected question out and re-insert as a
      // block at the drop position, preserving the selection's relative
      // order. Mirrors BidManageQuestionsTab's previous behaviour.
      setItems(prev => {
        const groupItems: FlatItem[] = []
        const remaining: FlatItem[] = []
        for (const it of prev) {
          if (it.type === 'question' && groupShas.has(it.sha)) groupItems.push(it)
          else remaining.push(it)
        }
        let newToIdx = remaining.length
        for (let i = toIdx; i < prev.length; i++) {
          const cand = prev[i]
          if (cand.type === 'question' && groupShas.has(cand.sha)) continue
          const ri = remaining.indexOf(cand)
          if (ri >= 0) { newToIdx = ri; break }
        }
        const next = [...remaining]
        next.splice(newToIdx, 0, ...groupItems)
        return next
      })
    } else {
      if (fromIdx === toIdx) { setOverIdx(null); return }
      setItems(prev => {
        const next = [...prev]
        const [moved] = next.splice(fromIdx, 1)
        next.splice(toIdx > fromIdx ? toIdx - 1 : toIdx, 0, moved)
        return next
      })
    }
    dragIdxRef.current = null
    dragGroupShasRef.current = null
    setOverIdx(null)
    onChange?.()
  }
  const handleDragEnd = () => {
    dragIdxRef.current = null
    dragGroupShasRef.current = null
    setOverIdx(null)
  }

  // ── Per-section helpers (selection + visibility) ────────────────────────
  const visibleSectionShas = (sectionIdx: number): string[] => {
    const out: string[] = []
    for (let i = sectionIdx + 1; i < items.length; i++) {
      const it = items[i]
      if (it.type === 'section') break
      const q = qMap[it.sha]
      if (!q) continue
      if (isQuestionVisible && !isQuestionVisible(q)) continue
      out.push(it.sha)
    }
    return out
  }
  const sectionAllSelected = (sectionIdx: number): boolean => {
    const shas = visibleSectionShas(sectionIdx)
    if (shas.length === 0) return false
    return shas.every(s => selectedShas.has(s))
  }
  const sectionAnySelected = (sectionIdx: number): boolean => {
    const shas = visibleSectionShas(sectionIdx)
    return shas.some(s => selectedShas.has(s))
  }
  const toggleSectionSelection = (sectionIdx: number) => {
    const shas = visibleSectionShas(sectionIdx)
    if (shas.length === 0) return
    const allIn = shas.every(s => selectedShas.has(s))
    shas.forEach(s => {
      const has = selectedShas.has(s)
      if (allIn && has) onToggleSelect(s)
      else if (!allIn && !has) onToggleSelect(s)
    })
  }

  // ── Ordinal + indent computation ────────────────────────────────────────
  const ordinalMap: Record<string, number> = {}
  const inSectionMap: Record<string, boolean> = {}
  {
    let ord = 0
    let inSection = false
    for (const item of items) {
      if (item.type === 'section') { inSection = true; continue }
      const q = qMap[item.sha]
      if (!q) continue
      if (isQuestionVisible && !isQuestionVisible(q)) continue
      ordinalMap[item.sha] = ++ord
      inSectionMap[item.sha] = inSection
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <>
      {items.map((item, idx) => {
        const isDropTarget = overIdx === idx && dragIdxRef.current !== null && dragIdxRef.current !== idx

        if (item.type === 'section') {
          const sec = sections.find(s => s.id === item.sectionId)
          if (!sec) return null
          const sectionShas = visibleSectionShas(idx)
          return (
            <Box
              key={`sec-${item.sectionId}`}
              data-testid={`${testIdPrefix}_section_row`}
              data-section-id={item.sectionId}
              draggable
              onDragStart={e => handleDragStart(e, idx)}
              onDragOver={e => handleDragOver(e, idx)}
              onDrop={e => handleDrop(e, idx)}
              onDragEnd={handleDragEnd}
              sx={{
                position: 'relative',
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 0.75, py: 0.5,
                bgcolor: 'action.selected',
                borderBottom: 1, borderColor: 'divider',
                cursor: 'grab', '&:active': { cursor: 'grabbing' },
              }}
            >
              {isDropTarget && <DropIndicator />}
              <DragIndicatorIcon sx={{ fontSize: ICON_SM, opacity: 0.4, flexShrink: 0 }} />
              {sectionShas.length > 0 && (
                <Checkbox
                  size="small"
                  data-testid={`${testIdPrefix}_section_checkbox`}
                  checked={sectionAllSelected(idx)}
                  indeterminate={!sectionAllSelected(idx) && sectionAnySelected(idx)}
                  onClick={e => { e.stopPropagation(); toggleSectionSelection(idx) }}
                  sx={{ p: 0.25 }}
                />
              )}
              <FolderOutlinedIcon sx={{ fontSize: ICON_SM, color: 'text.secondary', flexShrink: 0 }} />
              {editingSectionId === sec.id ? (
                <TextField
                  size="small"
                  autoFocus
                  value={editingName}
                  onChange={e => setEditingName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') commitRename()
                    if (e.key === 'Escape') setEditingSectionId(null)
                  }}
                  onBlur={commitRename}
                  inputProps={{ 'data-testid': `${testIdPrefix}_section_input` }}
                  sx={{ flex: 1, '& .MuiInputBase-input': { py: 0.15, px: 0.5, fontSize: FS_SM } }}
                />
              ) : (
                <Typography
                  data-testid={`${testIdPrefix}_section_name`}
                  variant="caption"
                  onClick={() => startRename(sec.id, sec.name)}
                  sx={{ flex: 1, fontWeight: 600, fontSize: FS_SM, cursor: 'text', '&:hover': { color: 'primary.main' } }}
                >
                  {sec.name}
                </Typography>
              )}
              {onDeleteSection && (
                <Tooltip title="Delete section">
                  <IconButton data-testid={`${testIdPrefix}_section_delete_btn`} size="small" onClick={() => onDeleteSection(sec.id)} sx={{ p: 0.25 }}>
                    <DeleteOutlineIcon sx={{ fontSize: ICON_SM }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )
        }

        const q = qMap[item.sha]
        if (!q) return null
        if (isQuestionVisible && !isQuestionVisible(q)) return null

        const qOrdinal = ordinalMap[item.sha] ?? 0
        const indented = !!inSectionMap[item.sha]
        const isSelected = selectedShas.has(item.sha)

        return (
          <Box
            key={item.sha}
            data-testid={`${testIdPrefix}_question_row`}
            data-sha={item.sha}
            data-in-section={indented ? 'true' : 'false'}
            data-selected={isSelected ? 'true' : 'false'}
            draggable
            onDragStart={e => handleDragStart(e, idx)}
            onDragOver={e => handleDragOver(e, idx)}
            onDrop={e => handleDrop(e, idx)}
            onDragEnd={handleDragEnd}
            sx={{
              position: 'relative',
              display: 'flex', alignItems: 'center', gap: 0.75,
              pr: 0.75, py: 0.4,
              pl: indented ? 3 : 0.75,
              borderBottom: 1, borderColor: 'divider',
              bgcolor: isSelected ? 'action.selected' : 'transparent',
              '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
            }}
          >
            {isDropTarget && <DropIndicator />}
            <DragIndicatorIcon sx={{ fontSize: ICON_SM, opacity: 0.3, flexShrink: 0, cursor: 'grab' }} />
            <Checkbox
              size="small"
              data-testid={`${testIdPrefix}_question_checkbox`}
              checked={isSelected}
              onClick={e => { e.stopPropagation(); onToggleSelect(item.sha) }}
              sx={{ p: 0.25 }}
            />

            {/* Ordinal + numbering - editable when onNumberingCommit is supplied. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
              {onNumberingCommit ? (
                <QuestionBadge
                  variant="inline"
                  numbering={q.numbering}
                  fallback={qOrdinal}
                  testIdPrefix={`${testIdPrefix}_numbering`}
                  onCommit={(v) => {
                    if ((q.numbering ?? '') === v) return
                    onNumberingCommit(item.sha, v)
                  }}
                />
              ) : (
                <QuestionBadge
                  variant="inline"
                  numbering={q.numbering}
                  fallback={qOrdinal}
                  testIdPrefix={`${testIdPrefix}_numbering`}
                  onCommit={() => { /* read-only when host doesn't supply a commit handler */ }}
                />
              )}
            </Box>

            {/* Question text - single-line truncated caption. */}
            <Typography
              variant="caption"
              component="div"
              sx={{ flex: 1, fontSize: FS_XS, opacity: 0.85, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              dangerouslySetInnerHTML={{ __html: q.question ?? '' }}
            />

            {rowEndSlot?.(q, item.sha)}
          </Box>
        )
      })}
    </>
  )
}
